import { inject, Injectable } from "@angular/core";
import { Device } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { DeviceSlotRole } from "@project/monitoring/monitoring-configuration.factory";
import { MonitoringSearchResult } from "@project/monitoring/monitoring-search-result";
import { forkJoin, map, Observable, take } from "rxjs";

export interface GroupSearchItem {
  option: FamilyStepOption;
  family: Family;
  category: MonitoringCategory;
  stepIndex?: number;
  canAddToFloorplan?: boolean;
}

export interface DeviceSearchItem {
  device: Device;
  option: FamilyStepOption;
  family: Family;
  category: MonitoringCategory;
  stepIndex?: number;
  canAddToFloorplan?: boolean;
  slotRole: DeviceSlotRole;
}

export interface MonitoringSearchData {
  groups: GroupSearchItem[];
  devices: DeviceSearchItem[];
  lookup: Map<string, Device>;
}

@Injectable({ providedIn: "root" })
export class MonitoringProductSearchService {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);

  loadSearchData(): Observable<MonitoringSearchData> {
    const filterCountry = this.productService.getFilterCountry();
    const categories = Object.values(MonitoringCategory);

    const familyRequests = categories.map((category) =>
      this.monitoringProductService.loadCategoryFamiliesFromDB(category, filterCountry),
    );
    const deviceRequests = categories.map((category) =>
      this.monitoringProductService.loadAllCategoryDevicesFromDB(category, filterCountry),
    );

    return forkJoin([...familyRequests, ...deviceRequests]).pipe(
      take(1),
      map((results: (Family[] | Device[])[]) => {
        const n = categories.length;
        const families = (results.slice(0, n) as Family[][]).flatMap((familyList, i) =>
          familyList.map((family) => ({ family, category: categories[i] })),
        );
        const devices = (results.slice(n) as Device[][]).flatMap((deviceList, i) =>
          deviceList.map((device) => ({ device, category: categories[i] })),
        );
        return this.flattenProducts(families, devices);
      }),
    );
  }

  search(
    query: string,
    groups: GroupSearchItem[],
    devicesWithContext: DeviceSearchItem[],
    deviceLookup: Map<string, Device>,
  ): MonitoringSearchResult[] {
    if (!query.trim()) return [];
    const q = query.toLocaleLowerCase();
    const groupResults = this.getSearchableGroups(q, groups, deviceLookup);
    const deviceResults = this.getSearchableDevices(q, devicesWithContext);
    return [...groupResults, ...deviceResults].sort(this.sortResults);
  }

  private getSearchableGroups(
    query: string,
    groups: GroupSearchItem[],
    deviceLookup: Map<string, Device>,
  ): MonitoringSearchResult[] {
    const seen = new Set<string>();
    return groups
      .filter(({ option }) => this.matchesGroup(option, query, deviceLookup))
      .sort((a, b) => (a.stepIndex ?? 0) - (b.stepIndex ?? 0))
      .filter(({ option, category }) => {
        const key = `${option.groupName}-${category}-${[...option.devices].sort().join(",")}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ option, family, category, stepIndex, canAddToFloorplan }) => ({
        kind: "group" as const,
        option,
        family,
        category,
        stepIndex,
        canAddToFloorplan,
      }));
  }

  private getSearchableDevices(query: string, devicesWithContext: DeviceSearchItem[]): MonitoringSearchResult[] {
    const seen = new Set<string>();
    return devicesWithContext
      .filter(({ device }) => this.matchesDevice(device, query))
      .sort((a, b) => (a.stepIndex ?? 0) - (b.stepIndex ?? 0))
      .filter(({ device, category, stepIndex }) => {
        const key = `${device.matNr}-${category}-${stepIndex}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ device, option, family, category, stepIndex, canAddToFloorplan, slotRole }) => ({
        kind: "device" as const,
        device,
        option,
        family,
        category,
        stepIndex,
        canAddToFloorplan,
        slotRole,
      }));
  }

  private sortResults = (a: MonitoringSearchResult, b: MonitoringSearchResult): number => {
    const nameA = a.kind === "group" ? a.option.groupName : a.device.name;
    const nameB = b.kind === "group" ? b.option.groupName : b.device.name;
    return nameA.localeCompare(nameB);
  };

  private matchesGroup(option: FamilyStepOption, query: string, deviceLookup: Map<string, Device>): boolean {
    if (option.groupName.toLocaleLowerCase().includes(query)) return true;
    return option.devices.some((matNr) => {
      if (matNr.toLocaleLowerCase().includes(query)) return true;
      const device = deviceLookup.get(matNr);
      return device ? this.matchesDevice(device, query) : false;
    });
  }

  private matchesDevice(device: Device, query: string): boolean {
    return device.name.toLocaleLowerCase().includes(query) || device.matNr.toLocaleLowerCase().includes(query);
  }

  private canAddStepToFloorplan(category: MonitoringCategory, family: Family, stepIndex: number): boolean {
    switch (category) {
      case MonitoringCategory.KVMS:
      case MonitoringCategory.DISPLAYS:
      case MonitoringCategory.UTILITY:
        return stepIndex === 0;

      case MonitoringCategory.PATIENT_MONITORS:
      case MonitoringCategory.TELEMETRY:
        return (
          stepIndex === 0 ||
          (stepIndex === 1 && family.steps[0]?.stepSelectionType === "PICK1" && family.steps[1]?.stepSelectionType === "PICK1")
        );

      case MonitoringCategory.CENTRAL_STATIONS:
        if (stepIndex === 0) return true;
        return family.steps[stepIndex]?.stepSelectionType === "PICK1";

      default:
        return false;
    }
  }

  private flattenProducts(
    families: { family: Family; category: MonitoringCategory }[],
    devices: { device: Device; category: MonitoringCategory }[],
  ): MonitoringSearchData {
    const groups = families.flatMap(({ family, category }) =>
      family.steps.flatMap((step, stepIndex) =>
        step.options
          .filter((option) => isGroupedOption(option))
          .map((option) => ({
            option,
            family,
            category,
            stepIndex,
            canAddToFloorplan: this.canAddStepToFloorplan(category, family, stepIndex),
          })),
      ),
    );

    const lookup = new Map<string, Device>();
    devices.forEach(({ device }) => lookup.set(device.matNr, device));

    const deviceItems = devices.flatMap(({ device, category }) => {
      const items: DeviceSearchItem[] = [];
      families
        .filter(({ category: familyCategory }) => familyCategory === category)
        .forEach(({ family }) => {
          family.steps.forEach((step, stepIndex) => {
            const slotRole = this.resolveSlotRoleForStep(category, stepIndex);
            step.options
              .filter((option) => !isGroupedOption(option))
              .forEach((option) => {
                if (option.devices.includes(device.matNr)) {
                  items.push({
                    device,
                    option,
                    family,
                    category,
                    stepIndex,
                    canAddToFloorplan: this.canAddStepToFloorplan(category, family, stepIndex),
                    slotRole,
                  });
                }
              });
          });
        });
      return items;
    });

    return { groups, devices: deviceItems, lookup };
  }

  private resolveSlotRoleForStep(category: MonitoringCategory, stepIndex: number): DeviceSlotRole {
    if (stepIndex === 0) return "head";
    if (category === MonitoringCategory.CENTRAL_STATIONS) return "licence";
    return "sensor";
  }
}
